import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';

import LegendIcon from '~/shared/assets/icons/line-chart-legend.svg';
import { useXFetch } from '~/shared/utils/useXFetch';
import runtimeEnv from '~/shared/utils/runtimeEnv';
import { Tenants } from '~/shared/utils/types';

import { datafyDevelopmentTenantId } from '../../common/helpers';

interface Labels {
  utilized: string;
  size: string;
  datafyDiskUsed: string;
}
interface LegendProps {
  onLabelToggle: (label: keyof Labels) => void;
  visibilityStatus: Record<keyof Labels, boolean>;
}

export default function Legends({ onLabelToggle, visibilityStatus }: LegendProps) {
  const [searchParams] = useSearchParams();
  const api = useXFetch();

  const { data: tenants } = useQuery({
    queryKey: ['getTenants'],
    queryFn: async () =>
      await api<Tenants>(
        `${runtimeEnv.fronteggBaseUrl}/frontegg/identity/resources/users/v3/me/tenants`
      ),
  });

  const showFullGraph =
    !!searchParams.get('full_graph') ||
    (tenants?.activeTenant?.tenantId &&
      datafyDevelopmentTenantId.includes(tenants?.activeTenant.tenantId));

  const iconColor = (currentColor: string, isVisible: boolean) => {
    return clsx('w-[24px] h-[24px]', isVisible ? currentColor : 'fill-[#E2E6EC]');
  };
  const fontColor = (isVisible: boolean) => {
    return clsx(
      'text-[16px] leading-[24px] font-bold text-[#949EAC] tracking-[-0.397px]',
      isVisible ? 'opacity-1' : 'opacity-40'
    );
  };
  return (
    <div
      id="legend-container"
      className="h-[36px] absolute bottom-[0px] mb-[-18px] w-full flex justify-center"
    >
      <div className="flex gap-[32px] h-fit items-center">
        {showFullGraph && (
          <div
            className="flex gap-[8px] h-fit items-center cursor-pointer"
            onClick={() => onLabelToggle('datafyDiskUsed')}
          >
            <LegendIcon className={iconColor('fill-[#A020F0]', visibilityStatus.datafyDiskUsed)} />
            <div className={fontColor(visibilityStatus.datafyDiskUsed)}>Datafy Utilized</div>
          </div>
        )}
        <div
          className="flex gap-[8px] h-fit items-center cursor-pointer"
          onClick={() => onLabelToggle('utilized')}
        >
          <LegendIcon className={iconColor('fill-[#06D727]', visibilityStatus.utilized)} />
          <div className={fontColor(visibilityStatus.utilized)}>Utilized</div>
        </div>
        <div
          className="flex gap-[8px] h-fit items-center cursor-pointer"
          onClick={() => onLabelToggle('size')}
        >
          <LegendIcon className={iconColor('fill-[#2151F5]', visibilityStatus.size)} />
          <div className={fontColor(visibilityStatus.size)}>Size</div>
        </div>
      </div>
    </div>
  );
}
